import React, { useState, useEffect } from "react";
import { ClientsFilter } from "elements/ClientsFilter";
import DatePicker from "react-datepicker";
import "./styles/datepicker.css";
import format from "date-fns/format";
import { useMira } from "components/GlobalMiraProvider";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputAdornment,
  Divider,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useGlobalToast } from "components/GlobalToastProvider";
import { X, CalendarDots, BoxArrowUp } from "@phosphor-icons/react";

const ExportSessionNotesModal = ({
  onChange,
  userId,
  userPermissions,
  classes,
  modalClientId,
  onClose,
  open,
  dateCreated,
  onDateChange,
  clientName,
}) => {
  const [isFromBeginningOfCare, setIsFromBeginningOfCare] = useState(true);
  const [dateError, setDateError] = useState(""); //tracks date input errors
  const [processError, setProcessError] = useState("");

  const { generate, miraState, setMiraReadyState } = useMira();
  const { showToast } = useGlobalToast();

  //function that triggers bulk_download process generation in the mira component.
  const generateProcess = async () => {
    if (!modalClientId) {
      setProcessError("Please provide a valid client.");
    }
    setProcessError("");

    const dateRange = isFromBeginningOfCare ? [] : dateCreated;
    const processData = {
      targetProcess: "bulk_download",
      platformContext: {
        resourceType: "client",
        resourceId: modalClientId,
        dateRange: dateRange,
        responseType: "zip",
      },
    };
    try {
      await generate(processData);
    } catch (error) {
      console.error(error);
      setProcessError("An error occurred during file download.");
    }
  };

  const validateDates = (startDate, endDate) => {
    const today = new Date();
    if (startDate && endDate && (startDate > today || endDate > today)) {
      setDateError("Please provide a valid date range.");
    } else {
      setDateError("");
    }
  };

  const handleModalClose = () => {
    if (miraState.processStatus === "in_progress") {
      return; // Prevent close if mira process is in progress
    }

    setDateError("");
    setProcessError("");
    setIsFromBeginningOfCare(true);
    setMiraReadyState(); // resets miraState to prepare for a new request

    if (onClose) {
      onClose();
    }
  };

  //response present with no error implies a successful download. Close modal and show success toast.
  useEffect(() => {
    if (miraState.response && !miraState.error) {
      showToast({ message: "Session notes successfully exported" });
      handleModalClose();
    }
    if (miraState.error) {
      setProcessError("An error occurred during the file download.");
    }
  }, [miraState.response, miraState.error]);

  // if dateCreated is pre defined, default radio button to custom date range and validate dates
  useEffect(() => {
    if (dateCreated[0] !== null && dateCreated[1] !== null) {
      setIsFromBeginningOfCare(false);
      validateDates(dateCreated[0], dateCreated[1]);
    }
  }, [dateCreated]);

  return (
    <Dialog open={open} onClose={handleModalClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4" fontWeight={500}>
          Export Session Notes
        </Typography>
        <IconButton onClick={handleModalClose}>
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <ClientsFilter
          userId={userId}
          clientId={modalClientId}
          userPermissions={userPermissions}
          onChange={(e) => onChange("modalClientId", e)}
          classes={classes}
          activeOnly
          label="Client"
          input={clientName}
          isInModal={true}
        />

        <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: "12px", mt: "24px" }}>
          Data Range
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            value={isFromBeginningOfCare}
            onChange={() => {
              setIsFromBeginningOfCare((prev) => {
                const next = !prev;

                if (next) {
                  // Switched to "From beginning of care"
                  setDateError("");
                } else {
                  // Switched to custom date range – validate the currently selected dates
                  validateDates(dateCreated[0], dateCreated[1]);
                }

                return next;
              });
            }}
          >
            <FormControlLabel
              value={true}
              control={<Radio color="secondary" size="small" />}
              label="From beginning of care"
              sx={{ fontSize: "14px" }}
            />
            <FormControlLabel
              value={false}
              control={<Radio color="secondary" size="small" />}
              label="Custom date range"
              sx={{ fontSize: "14px" }}
            />
          </RadioGroup>
        </FormControl>

        {!isFromBeginningOfCare && (
          <>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontSize: "12px", mt: "20px", mb: "8px" }}
            >
              Session Dates
            </Typography>
            <div id="billing-filters-container">
              <DatePicker
                selectsRange={true}
                startDate={dateCreated[0]}
                endDate={dateCreated[1]}
                onChange={onDateChange}
                placeholderText={format(new Date(), "MM/dd/yy")}
                popperPlacement="top-end"
                calendarClassName="modal-datepicker"
                customInput={
                  <TextField
                    sx={{ width: "395px" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarDots size={20} />
                        </InputAdornment>
                      ),
                    }}
                  />
                }
              />
            </div>
            {dateError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {dateError}
              </Typography>
            )}
          </>
        )}

        {processError && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {processError}
          </Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <LoadingButton
          color="primary"
          onClick={generateProcess}
          loading={miraState.processStatus === "in_progress"}
          disabled={
            !modalClientId ||
            dateError ||
            (!isFromBeginningOfCare && (dateCreated[0] === null || dateCreated[1] === null))
          }
          startIcon={<BoxArrowUp size={20} />}
          loadingPosition="start"
          sx={{ px: 2 }}
        >
          {miraState.processStatus === "in_progress" && !miraState.response
            ? "Exporting..."
            : "Export"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExportSessionNotesModal;
